/**
 * Unified configuration for all chatbot services
 * Single source of truth for environment-specific settings
 */

/**
 * Environment detection
 */
export const getEnvironment = () => {
  return window.location.hostname === 'localhost' ? 'development' : 'production';
};

const getBaseUrl = {
  localhost: 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edv.tenant-ai-1.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edv.tenant-ai-2.oneclicklabs.es': 'https://api-ai.tenant-ai-2.oneclicklabs.es',
  'tenant-ai-1.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'tenant-ai-2.oneclicklabs.es': 'https://api-ai.tenant-ai-2.oneclicklabs.es',
  'sumuncampus-co.sumunthebestyou.com': 'https://ia.sumuncampus-co.sumunthebestyou.com',
  'santillana-integrations.oneclicklabs.es': 'https://api-ai.santillana-integrations.oneclicklabs.es',
  'edelvivesdigital-dev.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edelvivesdigital-qa.oneclicklabs.es': 'https://api-ai.tenant-ai-1.oneclicklabs.es',
  'edelvivesdigitalplus.com': 'https://edelvivesdigitalplus.com',
  'edelvivesdigitalplus.com.mx': 'https://edelvivesdigitalplus.com.mx',
};

/**
 * Base configuration for all environments
 */
export const ENV_CONFIG = {
  development: {
    baseUrl: 'http://localhost:3000',
    apiPrefix: '/api/v1',
    defaultUserId: '6c10a08d-6ddf-40e9-8db5-a99588f14ff3', // Testing user ID
    isDev: true,
  },
  production: {
    baseUrl: getBaseUrl[window.location.hostname],
    apiPrefix: '/ai/v1',
    defaultUserId: null, // Must be provided in production
    isDev: false,
  },
};

/**
 * Get API URLs for current environment
 * @returns {Object} Object with URL builder functions
 */
export const getApiUrls = () => {
  const config = getEnvConfig();
  const baseUrl = `${config.baseUrl}${config.apiPrefix}`;

  return {
    // Chat URLs
    chat: {
      conversations: (mode) => `${baseUrl}/chat/${mode}`,
      conversation: (conversationId, mode) =>
        mode === 'teacher-tools' ? `${baseUrl}/chat/teacher-tools/conversations/${conversationId}` : `${baseUrl}/chat/${conversationId}`,
      messages: (conversationId) => `${baseUrl}/chat/${conversationId}`,
    },
    // Practice/Activities URLs
    activities: {
      generate: () => `${baseUrl}/activities/generate`,
      save: (activityInstanceId) => `${baseUrl}/activities/${activityInstanceId}`,
      generateSession: () => `${baseUrl}/activities/sessions`,
      getSessions: () => `${baseUrl}/activities/sessions`,
      checkSessions: () => `${baseUrl}/activities/sessions/check`,
      getActivityInstance: (sessionId, activityInstanceId) => `${baseUrl}/activities/sessions/${sessionId}/activities/${activityInstanceId}`,
      notifyError: () => `${baseUrl}/activities/notify-error`,
    },
    // Analytics URLs
    analytics: {
      classDiagnosticReport: (courseId) => `${baseUrl}/class-diagnostics/report${courseId ? `?courseId=${courseId}` : ''}`,
      eligibleSubjects: () => `${baseUrl}/subjects?hasActivities=true&includeDisciplines=true`,
    },
    // Teacher Tools URLs
    teacherTools: {
      getLessonContent: (lessonId) => `${baseUrl}/chat/teacher-tools/lessons/${lessonId}/content`,
      history: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return `${baseUrl}/chat/teacher-tools/history${queryString ? `?${queryString}` : ''}`;
      },
      // Teacher Tools URLs
      realWorldConnection: () => `${baseUrl}/chat/teacher-tools/real-world-connection`,
      textLevelAdapter: () => `${baseUrl}/chat/teacher-tools/text-level-adaptation`,
      lessonPlanGenerator: () => `${baseUrl}/chat/teacher-tools/lesson-plan-generator`,
      rubricGenerator: () => `${baseUrl}/chat/teacher-tools/rubric-generator`,
      vocabularyCreator: () => `${baseUrl}/chat/teacher-tools/vocabulary-creator`,
      textTranslator: () => `${baseUrl}/chat/teacher-tools/text-translator`,
      studentFeedback: () => `${baseUrl}/chat/teacher-tools/student-feedback`,
      questionGenerator: () => `${baseUrl}/chat/teacher-tools/question-generator`,
      classroomDynamics: () => `${baseUrl}/chat/teacher-tools/classroom-dynamics`,
      activityTypes: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return `${baseUrl}/activity-types${queryString ? `?${queryString}` : ''}`;
      },
    },
    // Conversations URLs
    conversations: {
      quizRevision: () => `${baseUrl}/chat/revision/conversations`,
      quizRevisionLookup: (quizJobGuid, questionReferenceId) => `${baseUrl}/chat/revision/jobs/${quizJobGuid}/questions/${questionReferenceId}/conversation`,
      quizRevisionRegenerate: () => `${baseUrl}/chat/revision/regenerate`,
    },
  };
};

/**
 * Get configuration for current environment
 * @param {string} environment - Optional environment override
 * @returns {Object} Environment configuration
 */
export const getEnvConfig = (environment) => {
  const env = environment || getEnvironment();
  return ENV_CONFIG[env] || ENV_CONFIG.development;
};

/**
 * Get user ID from localStorage (shared logic)
 * @returns {string|null} User ID
 */
export const getUserIdFromStorage = () => {
  try {
    // Get user data from the persisted Redux store
    const persistedAuth = localStorage.getItem('persist:i2c_auth');
    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);
      if (authData && authData.user) {
        const userData = JSON.parse(authData.user);
        if (userData && userData.guid) {
          return userData.guid;
        }
      }
    }

    // Use default user ID as fallback
    const environment = getEnvironment();
    const config = getEnvConfig();
    const fallbackId = config.defaultUserId;

    // In production, log warning if no user ID is found
    if (environment === 'production' && !fallbackId) {
      console.warn('No user ID found and no fallback provided in production environment');
    }

    return fallbackId;
  } catch (error) {
    console.error('Error retrieving user ID:', error);
    const config = getEnvConfig();
    return config.defaultUserId;
  }
};

/**
 * Get auth token from localStorage (similar to user ID retrieval)
 * @returns {string|null} Auth token
 */
export const getAuthTokenFromStorage = () => {
  try {
    // Get auth data from the persisted Redux store
    const persistedAuth = localStorage.getItem('persist:i2c_auth');

    if (persistedAuth) {
      const authData = JSON.parse(persistedAuth);

      if (authData && authData.authToken) {
        // Parse the token if it's stringified
        const token = typeof authData.authToken === 'string' && authData.authToken.startsWith('"') ? JSON.parse(authData.authToken) : authData.authToken;
        return token;
      }
    }
    return null;
  } catch (error) {
    console.error('Error retrieving auth token:', error);
    return null;
  }
};

/**
 * Create common headers for API requests
 * @param {Object} additionalHeaders - Additional headers to include
 * @param {Function} customGetAuthToken - Optional custom auth token getter (for services that need override)
 * @returns {Object} Headers object
 */
export const createHeaders = (additionalHeaders = {}, customGetAuthToken = null) => {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  // Get auth token - use custom getter if provided, otherwise use default
  const authToken = customGetAuthToken ? customGetAuthToken() : getAuthTokenFromStorage();
  if (authToken) {
    headers['Authorization'] = authToken;
  }
  return headers;
};

/**
 * Default error handler
 * @param {string} serviceName - Name of the service for logging
 * @param {Error} error - Error object
 */
export const defaultErrorHandler = (serviceName, error) => {
  console.error(`${serviceName} error:`, error);
};
